import { InferType } from 'yup';
import { commentsSchema } from '../schema/comments.schema.ts';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useEffect, useState } from 'react';
import {
  useDeleteTaskCommentMutation,
  useEditTaskCommentMutation,
} from '@common/cache';

type IFormShape = InferType<typeof commentsSchema>;

const initialValues = {
  comment: '',
};

export const useComment = ({
  comment,
  comment_id,
  task_id,
  handleRemoveCommentToEdit,
  setIsFormClean,
  newCommentToEditId,
  setNewCommentToEditId,
  handleSetCommentToEdit,
  isFormClean,
  triggerTabChange,
  setTriggerTabChange,
  setTriggerOpenCreateComment,
  triggerOpenCreateComment,
  handleOpenCreateComment,
  commentToEditId,
  closeDrawer,
  triggerCloseDrawer,
  setTriggerCloseDrawer,
}: {
  comment: string;
  comment_id: string;
  task_id: string;
  isFormClean: boolean;
  handleRemoveCommentToEdit: () => void;
  setIsFormClean: (clean: boolean) => void;
  newCommentToEditId: string;
  commentToEditId: string;
  setNewCommentToEditId: (id: string) => void;
  handleSetCommentToEdit: (id: string) => void;
  triggerTabChange: boolean;
  setTriggerTabChange: (trigger: boolean) => void;
  setTriggerOpenCreateComment: (trigger: boolean) => void;
  handleOpenCreateComment: () => void;
  triggerOpenCreateComment: boolean;
  closeDrawer: () => void;
  triggerCloseDrawer: boolean;
  setTriggerCloseDrawer: (trigger: boolean) => void;
}) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);

  const { mutateAsync: editCommentAsync, isPending: isSubmitting } =
    useEditTaskCommentMutation({ comment_id, task_id });

  const { mutateAsync: deleteCommentAsync, isPending: isDeleting } =
    useDeleteTaskCommentMutation({
      comment_id,
      task_id,
    });

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(commentsSchema),
    defaultValues: {
      ...initialValues,
      comment,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = formMethods;

  const handleOpenConfirmDeleteModal = () => {
    setIsConfirmDeleteModalOpen(true);
  };

  const handleCloseConfirmDeleteModal = () => {
    setIsConfirmDeleteModalOpen(false);
  };

  const handleSetCommentToEditAction = () => {
    if (isFormClean) {
      handleSetCommentToEdit(comment_id);
    } else {
      setNewCommentToEditId(comment_id);
    }
  };

  const handleCloseEditCommentAction = () => {
    handleRemoveCommentToEdit();
    reset({ comment });
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await editCommentAsync({ comment: values.comment || '' });

      reset(values);
      handleRemoveCommentToEdit();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteCommentAsync();

      setIsConfirmDeleteModalOpen(false);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    setNewCommentToEditId('');
    setTriggerTabChange(false);
    setTriggerOpenCreateComment(false);
    setTriggerCloseDrawer(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    reset({ comment });

    if (triggerTabChange) {
      setIsFormClean(true);
      setTriggerTabChange(false);
    }
    if (triggerCloseDrawer) {
      setTriggerCloseDrawer(false);
      closeDrawer();
    }
    if (triggerOpenCreateComment) {
      handleOpenCreateComment();
    }
    if (newCommentToEditId) {
      handleSetCommentToEdit(newCommentToEditId);
      setNewCommentToEditId('');
    }
  };

  const handleSaveChanges = async () => {
    await onSubmit();
    if (newCommentToEditId) {
      handleSetCommentToEdit(newCommentToEditId);
      setNewCommentToEditId('');
    }
    if (triggerTabChange) {
      setIsFormClean(true);
      setTriggerTabChange(false);
    }
    if (triggerCloseDrawer) {
      setTriggerCloseDrawer(false);
      closeDrawer();
    }
    if (triggerOpenCreateComment) {
      handleOpenCreateComment();
    }
    setIsSaveChangesModalOpen(false);
  };

  useEffect(() => {
    setIsFormClean(!isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (newCommentToEditId && commentToEditId === comment_id && !isFormClean) {
      setIsSaveChangesModalOpen(true);
    }
  }, [newCommentToEditId]);

  useEffect(() => {
    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
    }
  }, [triggerTabChange]);

  useEffect(() => {
    if (triggerCloseDrawer && isDirty) {
      setIsSaveChangesModalOpen(true);
    }
  }, [triggerCloseDrawer]);

  useEffect(() => {
    if (triggerOpenCreateComment && isDirty) {
      setIsSaveChangesModalOpen(true);
    }
  }, [triggerOpenCreateComment]);

  return {
    onSubmit,
    formMethods,
    isValid,
    isSubmitting,
    isDeleting,
    handleOpenConfirmDeleteModal,
    handleCloseConfirmDeleteModal,
    confirmDelete,
    isConfirmDeleteModalOpen,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    handleSetCommentToEditAction,
    handleCloseEditCommentAction,
  };
};
